/**
 * JSON-LD builders.
 *
 * Ground rule: every statement here must already be true on the site or come
 * from the published contact details. No invented awards, ratings, review
 * counts, addresses, founding dates or headcount. In particular we never emit
 * an AggregateRating: without real, verifiable reviews it is a manual-action
 * risk with Google.
 *
 * The Organization and WebSite nodes carry stable @id anchors so every other
 * node can reference them instead of repeating the object.
 */
import config from './config';
import routes from './routes';

const { SITE_URL, SITE_NAME, LOGO_PATH, CONTACT_EMAIL, CONTACT_WHATSAPP, absoluteUrl } = config;
const { localizedPath } = routes;

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const ORG_DESCRIPTION = {
  en:
    'E-comienza is an AI studio for education that builds course chatbots inside Moodle. The ' +
    'agent answers student questions using only the course material, cites the unit and page, ' +
    'and is available 24 hours a day. The studio also builds agents for WhatsApp and the web.',
  es:
    'E-comienza es un estudio de IA para educación que construye chatbots de curso dentro de ' +
    'Moodle. El agente responde las dudas de los alumnos solo con el material del curso, cita la ' +
    'unidad y la página, y está disponible las 24 horas. El estudio también construye agentes ' +
    'para WhatsApp y la web.',
};

/**
 * Publicly linked profiles only. Do not add entries here unless the profile is
 * actually owned by E-comienza and was supplied or published by the business.
 */
const SAME_AS = [
  'https://www.instagram.com/ecomienza/',
  'https://www.linkedin.com/company/e-comienza',
];

/**
 * The two founders, supplied by the client.
 *
 * Only what was supplied is emitted. No `jobTitle`: none was given, and a title
 * invented for a named real person is exactly the kind of unverifiable claim
 * this file refuses everywhere else. `foundingDate` and a location are still
 * unknown and are likewise absent from the Organization node.
 *
 * The names are spelled as the client wrote them, without diacritics. If a
 * correction comes back, it is a one-line change here.
 */
export const FOUNDER_IDS = {
  andresPrato: `${SITE_URL}/#andres-prato`,
  nicolasTredinick: `${SITE_URL}/#nicolas-tredinick`,
};

const FOUNDERS = [
  {
    id: FOUNDER_IDS.andresPrato,
    name: 'Andres Prato',
    sameAs: ['https://www.linkedin.com/in/andres-prato/'],
  },
  {
    id: FOUNDER_IDS.nicolasTredinick,
    name: 'Nicolas Tredinick',
    sameAs: ['https://www.linkedin.com/in/nicolas-t-67221b181/'],
  },
];

/**
 * One Person node per founder. They are emitted on every page, because the
 * Organization node references them by @id from `founder` and a reference to an
 * @id that is not in the same @graph resolves to nothing.
 */
export function founderSchemas() {
  return FOUNDERS.map((founder) => ({
    '@type': 'Person',
    '@id': founder.id,
    name: founder.name,
    // No `url` and no `jobTitle`: neither was supplied, and the company home
    // page is not this person's URL.
    sameAs: founder.sameAs,
    worksFor: { '@id': ORGANIZATION_ID },
  }));
}

/** Organization node. Referenced everywhere else by @id. */
export function organizationSchema(lang = 'en') {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE_URL + '/',
    logo: {
      '@type': 'ImageObject',
      url: absoluteUrl(LOGO_PATH),
    },
    image: absoluteUrl(LOGO_PATH),
    description: ORG_DESCRIPTION[lang] || ORG_DESCRIPTION.en,
    email: CONTACT_EMAIL,
    telephone: CONTACT_WHATSAPP,
    // Services are delivered remotely; the site is published in English and Spanish.
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    knowsLanguage: ['en', 'es'],
    // Only profiles that are actually linked from the site. Empty is better than invented.
    sameAs: SAME_AS,
    // The Person nodes themselves are emitted alongside this one; see founderSchemas().
    founder: FOUNDERS.map((founder) => ({ '@id': founder.id })),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: CONTACT_WHATSAPP,
        email: CONTACT_EMAIL,
        availableLanguage: ['en', 'es'],
        areaServed: 'Worldwide',
      },
    ],
  };
}

/** WebSite node. */
export function websiteSchema(lang = 'en') {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_URL + '/',
    name: SITE_NAME,
    alternateName: 'E-comienza AI for Education',
    description: ORG_DESCRIPTION[lang] || ORG_DESCRIPTION.en,
    inLanguage: lang === 'es' ? 'es' : 'en',
    publisher: { '@id': ORGANIZATION_ID },
  };
}

/**
 * The three services described on the home page ("What We Build" tiles).
 * Wording mirrors the copy that is actually rendered.
 */
const SERVICES = [
  {
    slug: 'moodle-course-chatbot',
    // The page that describes this service. Falls back to the home page anchor.
    routeKey: 'moodleAgent',
    en: {
      name: 'Moodle Course Chatbot',
      description:
        'The agent answers from your course material and nothing else. Every answer cites the ' +
        'unit and the page, and it says so when a question is not covered in the course.',
    },
    es: {
      name: 'Chatbot para cursos en Moodle',
      description:
        'El agente responde con el material de tu curso y nada más. Cada respuesta cita la ' +
        'unidad y la página, y si la pregunta no está en el curso, lo dice.',
    },
  },
  {
    slug: 'native-moodle-integration',
    routeKey: 'moodleAgent',
    en: {
      name: 'Native Moodle Integration',
      description:
        'The agent lives in the Moodle course your students already open. Nothing to install, ' +
        'no app to switch to, no extra platform to administer.',
    },
    es: {
      name: 'Integración nativa en Moodle',
      description:
        'El agente vive en el curso de Moodle que tus alumnos ya abren. Nada que instalar, ' +
        'ninguna app a la que cambiar, ninguna plataforma extra que administrar.',
    },
  },
  {
    slug: 'moodle-agent-deployment',
    routeKey: 'moodleAgent',
    en: {
      name: 'Moodle Agent Deployment',
      description:
        'We connect the course, index the material, and the agent goes live inside Moodle, ' +
        'answering your students 24 hours a day.',
    },
    es: {
      name: 'Despliegue del agente en Moodle',
      description:
        'Conectamos el curso, indexamos el material y el agente queda activo dentro de Moodle, ' +
        'respondiendo a tus alumnos las 24 horas.',
    },
  },
];

/** One ProfessionalService node per service on the home page. */
export function serviceSchemas(lang = 'en') {
  const key = lang === 'es' ? 'es' : 'en';
  return SERVICES.map((service) => ({
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#service-${service.slug}`,
    name: service[key].name,
    description: service[key].description,
    serviceType: service.en.name,
    provider: { '@id': ORGANIZATION_ID },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    availableLanguage: ['en', 'es'],
    // Point at the page that describes the service where one exists.
    url: service.routeKey
      ? SITE_URL + localizedPath(service.routeKey, lang)
      : SITE_URL + localizedPath('home', lang) + '#work',
  }));
}

/**
 * BreadcrumbList for non-home pages.
 * `items` is [{ name, key }] with route keys; "Home" is prepended.
 */
export function breadcrumbSchema(items = [], lang = 'en') {
  const homeName = lang === 'es' ? 'Inicio' : 'Home';
  const trail = [{ name: homeName, key: 'home' }].concat(items);
  const lastKey = items.length ? items[items.length - 1].key : 'home';
  return {
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}${localizedPath(lastKey, lang)}#breadcrumb`,
    itemListElement: trail.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: SITE_URL + localizedPath(item.key, lang),
    })),
  };
}

/**
 * FAQPage built from a page's own `faqs` array (src/seo/content.js).
 * The same questions and answers are rendered as text on the page: marking up
 * an answer that is not visible is a structured-data violation.
 */
export function faqSchema(faqs = [], routeKey = 'home', lang = 'en') {
  return {
    '@type': 'FAQPage',
    '@id': `${SITE_URL}${localizedPath(routeKey, lang)}#faq`,
    inLanguage: lang === 'es' ? 'es' : 'en',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * The nodes every page of the site declares: the Organization, the WebSite and
 * the two founder Person nodes the Organization points at. Every builder below
 * starts from this, so a `founder` reference never dangles.
 */
export function siteSchemas(lang = 'en') {
  return [organizationSchema(lang), websiteSchema(lang)].concat(founderSchemas());
}

/**
 * Everything the home page should declare. No FAQPage here: the home view does
 * not render an FAQ block, and marking up questions that are not on the page is
 * a structured-data violation.
 */
export function homeJsonLd(lang = 'en') {
  return siteSchemas(lang).concat(serviceSchemas(lang));
}

/** Everything a simple content page should declare. */
export function pageJsonLd(lang = 'en', breadcrumbItems = []) {
  const nodes = siteSchemas(lang);
  if (breadcrumbItems.length) nodes.push(breadcrumbSchema(breadcrumbItems, lang));
  return nodes;
}

/**
 * Everything one of the long-form content pages declares: the site-wide
 * Organization and WebSite nodes, the page's FAQPage and a Home > page
 * BreadcrumbList.
 */
export function contentPageJsonLd(routeKey, page, lang = 'en') {
  const nodes = siteSchemas(lang);
  nodes.push(breadcrumbSchema([{ name: page.h1, key: routeKey }], lang));
  if (page.faqs && page.faqs.length) nodes.push(faqSchema(page.faqs, routeKey, lang));
  return nodes;
}

/**
 * DefinedTermSet + one DefinedTerm per glossary entry.
 *
 * This is the type answer engines read for a glossary: it says "this page
 * defines these terms" explicitly, term by term, instead of leaving a crawler to
 * infer it from headings. Every term also gets its own `url` with the in-page
 * anchor, so an engine can cite a single definition rather than the whole page.
 *
 * `entries` is `seoContent[lang].glossary`: the same `{ term, slug, definition }`
 * objects the page renders as visible text.
 */
export function definedTermSetSchema(entries = [], meta = {}, lang = 'en') {
  const pageUrl = SITE_URL + localizedPath('glossary', lang);
  const setId = `${pageUrl}#glossary`;
  return {
    '@type': 'DefinedTermSet',
    '@id': setId,
    name: meta.name,
    description: meta.description,
    url: pageUrl,
    inLanguage: lang === 'es' ? 'es' : 'en',
    publisher: { '@id': ORGANIZATION_ID },
    hasDefinedTerm: entries.map((entry) => ({
      '@type': 'DefinedTerm',
      '@id': `${pageUrl}#${entry.slug}`,
      name: entry.term,
      description: entry.definition,
      url: `${pageUrl}#${entry.slug}`,
      inDefinedTermSet: { '@id': setId },
    })),
  };
}

/** Everything the glossary page declares. */
export function glossaryJsonLd(entries, meta, lang = 'en') {
  return siteSchemas(lang).concat([
    breadcrumbSchema([{ name: meta.breadcrumb, key: 'glossary' }], lang),
    definedTermSetSchema(entries, meta, lang),
  ]);
}

/**
 * Everything a case-study page declares: the site-wide nodes, a Home > case
 * BreadcrumbList and the page's own FAQPage, built from the same `faqs` the
 * page renders.
 */
export function caseStudyJsonLd(routeKey, study, lang = 'en') {
  const nodes = siteSchemas(lang);
  nodes.push(breadcrumbSchema([{ name: study.title, key: routeKey }], lang));
  if (study.faqs && study.faqs.length) nodes.push(faqSchema(study.faqs, routeKey, lang));
  return nodes;
}
